import { IActiveTier, IListItem, StateInterface } from '../interfaces/interfaces';
import { applyJPlist } from '../../../domain/jplist/jplist';
import { generateDeepLink, updateDeepLink, getUrlParams } from './deep-link-provider';
import { getFromStorage } from './list-page-storage-provider';
import { getCategoryFromURL, sendGAEvent, sendGaEventImpressions } from '../../../domain/marketing/gtm-provider';
import { getJSONFromHtml } from '../../../domain/common';
import { DESKTOP_BREAKPOINT, MOBILE_BREAKPOINT } from '../../../domain/animations/animations-provider';
import { IPurchased, IPurchasedProduct } from '../../membership/purchased-products/interfaces';

export const HIDE_MY_PRODUCTS_SESSION_STORAGE_KEY = 'hide-my-products';

// if window width is <= LIST_TO_GRID_ENDPOINT => list view automatically will be converted to the grid view
export const LIST_TO_GRID_ENDPOINT = 800;

// items per page
export const ITEMS_PER_PAGE_1 = 18;
export const ITEMS_PER_PAGE_2 = 42;
export const ITEMS_PER_PAGE_3 = 60;
export const ITEMS_PER_PAGE_ALL = 10000;

// ------------------------- DATA -----------------------------

/**
 * normalize product
 */
export const normalizeProduct = (product: IListItem) => {

    if(!product) return product;

    // properties with inner HTML - normalizations
    // product.note = product.note ? GeneralHelper.decodeURL(product.note) : '';
    // product.documentPageDescription = product.documentPageDescription ? GeneralHelper.decodeURL(product.documentPageDescription) : '';

    // computed properties
    product.withCouponEnabled = product.couponCampaignID > 0;
    product.isProductVisible = product.skuid > 0 && product.skuEnabled;
    product.isSamePrice = product.msrp === product.skuPrice;
    product.minPrice = !!product.note ? 'fake' : (product.couponPrice > 0 ? Math.min(product.skuPrice, product.couponPrice) : product.skuPrice);

    // https://wavesaudio.atlassian.net/browse/DSWEB-1264
    // Edited by Raz Liveyatan, 06.06.2021
    product.expectedShipping = product.expectedShipping !== '' ? product.expectedShipping : null;

    return product;
};

/**
 * products data is located in HTML in DIV with id 'list-data'
 */
export const getProductsData = () : IListItem[] => {

    const data = getJSONFromHtml(document.getElementById('list-data')) as IListItem[]
    if(!data || !Array.isArray(data)) return [];

    for(let i=0; i<data.length; i++){
        data[i] = normalizeProduct(data[i]);
    }

    return data;
};

/**
 * https://wavesaudio.atlassian.net/browse/DSWEB-1738
 */
export const getSubscriptionActiveTiers = () => {
    return (getJSONFromHtml(document.getElementById('sbs-active-tiers')) as IActiveTier[]) ?? [];
};

// ------------------------- MAIN -----------------------------

/**
 * refresh jPList according to the current state
 */
export const refresh = (state: StateInterface, shouldUpdateDeepLink: boolean = true) => {

    const res = applyJPlist(
        state.productsInitial,

        // filter
        state.filterTerm,
        state.filterProperty,

        // pagination
        state.currentPage,
        state.itemsPerPage,
        state.paginationRange,

        // sort
        state.sortType, // text|number|datetime
        state.sortProperty === 'skuPrice' ? 'minPrice' : state.sortProperty,
        state.sortOrder, // asc|desc
        'mainOrder',
        state.hideMyProducts ? state.purchased : null,
    );

    if(shouldUpdateDeepLink) {

        // handle deep link: update deep link on each jPList change
        const deepLink = generateDeepLink(
            state.sortProperty,
            state.sortType,
            state.sortOrder,
            state.currentPage,
            state.itemsPerPage,
            state.layoutMode,
            state.filterTerm);

        updateDeepLink(deepLink);
    }

    return res;
};

/**
 * init deep link on page load;
 * parse the existing deep link, and add it to the current state
 */
export const deepLinkAndStorageInit = (state: StateInterface, showPriceSorting: boolean) => {

    // deep link
    const paramsMap = getUrlParams(window.location.hash);
    const props = ['sortProperty', 'sortType', 'sortOrder', 'currentPage', 'itemsPerPage', 'layoutMode', 'filterTerm'];

    // storage data
    const data = getFromStorage() as unknown as StateInterface;

    for(let i=0; i<props.length; i++){
        const prop = props[i];

        if(paramsMap.has(prop)){
            state[prop] = paramsMap.get(prop);
        }
        else{
            if(data && data[prop]){
                state[prop] = data[prop];
            }
        }
    }

    if(!showPriceSorting && state.sortProperty === 'skuPrice' && state.sortType === 'number'){
        state.sortProperty = 'mainOrder';
        state.sortType = 'text';
    }
};

/**
 * init sort data from a/b test
 */
export const initSortDataABTest = async (
        state: StateInterface,
        updateInitialProducts: Function
    ) => {

    /*
    // Removed because of https://wavesaudio.atlassian.net/browse/DSWEB-1934
    if(state.isRecommended){
        const products = [...state.productsInitial];

        if(state.isRecommended && !state.isRecommendedPopUp && products && products.length > 0 && getIsAuthenticated()){
            sendGAEvent('My Recommended Products Page','Products');
        }
        return;
    }*/

    const products = JSON.parse(JSON.stringify(state.productsInitial)) as IListItem[];
    const updatedProducts = await setRecommendedProductsOrder(products, state.purchased);
    updateInitialProducts(updatedProducts);
};

/**
 * send analytics data on  page load
 */
export const sendInitialAnalytics = (sortProperty: string, stateProducts: IListItem[], hideMyProducts: boolean) => {
    sendGAEvent('JPList', `Products I Have: ${ hideMyProducts ? 'Hide' : 'Show' }`);

    if (sortProperty === 'mainOrder') {

        // get first 20 items
        const products = stateProducts.slice(0, 20);

        const impressions = products.map((product, index) => {
            return {
                name: product.documentName,
                id: product.skuNumber,
                price: product.skuPrice,
                category: getCategoryFromURL(product.documentUrlPath),
                variant: 'NONE',
                list: 'List Page',
                position: index + 1,
            };
        });

        sendGaEventImpressions(impressions);
    }
};

// ------------------------- UI -----------------------------

/**
 * set equal height for every element in the group the highest one
 */
const setGroupHeight = (group: HTMLElement[][]) => {
    if(!group || group.length <= 1) return;

    let taglinesMaxHeight = 0;
    let titlesMaxHeight = 0;
    let starRatingsMaxHeight = 0;
    let sbsTextMaxHeight = 0;

    group.forEach(item => {
        const $tagline = item[0];
        const $title = item[1];
        const $starRating = item[2];
        const $sbsText = item[3];

        taglinesMaxHeight = $tagline ? Math.max(taglinesMaxHeight, $tagline.offsetHeight) : 0;
        titlesMaxHeight = $title ? Math.max(titlesMaxHeight, $title.offsetHeight) : 0;
        starRatingsMaxHeight = $starRating ? Math.max(starRatingsMaxHeight, $starRating.offsetHeight) : 0;
        sbsTextMaxHeight = $sbsText ? Math.max(sbsTextMaxHeight, $sbsText.offsetHeight) : 0;
    });

    group.forEach(item => {
        const $tagline = item[0];
        const $title = item[1];
        const $starRating = item[2];
        const $sbsText = item[3];

        if($tagline && taglinesMaxHeight > 0)  $tagline.style.height = `${ taglinesMaxHeight }px`;
        if($title && titlesMaxHeight > 0) $title.style.height = `${ titlesMaxHeight }px`;
        if($starRating && starRatingsMaxHeight > 0) $starRating.style.height = `${ starRatingsMaxHeight }px`;
        if($sbsText && sbsTextMaxHeight > 0) $sbsText.style.height = `${ sbsTextMaxHeight }px`;
    });
};

/**
 * titles and taglines should have equal height in grid view
 */
export const initEqualHeight = (
    $container: HTMLDivElement|null,
    width: number,
    layoutMode: string,
    desktopSize: number = 3,
    tabletSize: number = 2,
    mobileSize: number = 2) : Promise<void> => {

    return new Promise((resolve, reject) => {

        if(!$container){
            resolve();
            return;
        }

        window.setTimeout(() => {

            // remove height property from all current (for mobile, list layout, etc.)
            const $taglines = $container.querySelectorAll('[data-grid-tagline="true"]') as NodeListOf<HTMLElement>;
            $taglines.forEach($tagline => {
                $tagline.style.height = '';
            });

            const $titles = $container.querySelectorAll('[data-grid-title="true"]') as NodeListOf<HTMLElement>;
            $titles.forEach($title => {
                $title.style.height = '';
            });

            const $starRatings = $container.querySelectorAll('[data-grid-stars="true"]') as NodeListOf<HTMLElement>;
            $starRatings.forEach(starRating => {
                starRating.style.height = '';
            });

            const $soldMsg = $container.querySelectorAll('[data-grid-soldmsg="true"]') as NodeListOf<HTMLElement>;
            $soldMsg.forEach(soldMsg => {
                soldMsg.style.height = '';
            });

            const $sbsTexts = $container.querySelectorAll('[data-grid-sbs-text="true"]') as NodeListOf<HTMLElement>;
            $sbsTexts.forEach($sbsText => {
                $sbsText.style.height = '';
            });

            // in list view and in mobile -> no need to define the special heights
            if(
                // in case we have a mobile viewport, but only one grid item in the row (like in all list pages) ---> stop
                (mobileSize <= MOBILE_BREAKPOINT && mobileSize === 1) ||
                (layoutMode !== 'grid')){
                resolve();
                return;
            }

            // on list page, for example: 3 items per row in desktop, 2 items per row in tablet
            let groupSize = desktopSize;
            if(width <= MOBILE_BREAKPOINT){
                groupSize = mobileSize;
            }
            else if(width < DESKTOP_BREAKPOINT){
                groupSize = tabletSize;
            }

            // there is the same number of titles & taglines (equals to the # of products)
            const n = $titles.length;

            let group: HTMLElement[][] = [];
            let i=0;

            while(i < n){

                if(i % groupSize === 0){
                    setGroupHeight(group);
                    // new group start
                    group = [];
                }

                group.push([$taglines[i], $titles[i], $starRatings[i],$soldMsg[i], $sbsTexts[i]]);
                i++;
            }

            setGroupHeight(group);

            resolve();
        }, 0);
    });
};

// ------------------------- HELPERS -----------------------------

/**
 * get initial value of 'hideMyProducts'
 */
export const getHideMyProducts = () => {
    const sessionStorageString = window.sessionStorage.getItem(HIDE_MY_PRODUCTS_SESSION_STORAGE_KEY);
    const sessionStorageExists = sessionStorageString !== null;
    const sessionStorageBool = sessionStorageString === 'true';
    return sessionStorageExists ? sessionStorageBool : false;
};

export const getShowAllProductsToCartBtnLink = (productsInitial: IListItem[]) : string => {
    if(!productsInitial || productsInitial.length <= 1) return '';

    const $box = document.getElementById('list-data');
    if(!$box) return '';

    if(!$box.hasAttribute('data-all-products-to-cart')) return '';

    // /cart?sku477=2&sku355=3
    const qs = productsInitial.map(item => `sku${ item.skuid }=1`).join('&');
    return `/cart?${ qs }`;
};

/**
 * get filter placeholder text from the hidden DIV
 * with data-attribute [data-jplist-search-title]
 * the DIV is placed in the top code mirror on the page as a plain HTML
 */
export const getFilterPlaceholderText = () => {

    let filterPlaceholderText = 'Search';
    const $filterTextEl = document.querySelector('[data-jplist-search-title]');

    if($filterTextEl){
        filterPlaceholderText = $filterTextEl.getAttribute('data-jplist-search-title') ?? '';
    }

    return filterPlaceholderText;
};


/**
 * set recommended products order in the jplist
 * https://wavesaudio.atlassian.net/browse/DSWEB-1632
 */
export const setRecommendedProductsOrder = async (_products: IListItem[], purchasedData: IPurchased|null) => {

    const products = JSON.parse(JSON.stringify(_products)) as IListItem[];
    const purchased = purchasedData?.purchasedProducts || [];
    const recommended = purchasedData?.recommendedProducts || [];

    const arr = [];

    // not purchased products
    const notPurchased = products.filter(item => {
        const itemIndex = purchased.findIndex((r: IPurchasedProduct) => r.skuNumber === item.skuNumber);
        const isPurchased = itemIndex !== -1;
        const contentType = isPurchased ? purchased[itemIndex].contentType : 0;
        return (!isPurchased || (isPurchased && contentType === 4));
    });

    // not purchased bundles and manual order
    for(let i=0; i<notPurchased.length; i++){
        const product = notPurchased[i];

        if(product.fixedPosition || product.mainOrder < 11){
            arr[i] = product.skuNumber;
        }
    }
    console.log('not purchased bundles and manual order', arr);

    // add recommended items to their places
    for (let i=0; i<recommended.length; i++){
        const recItem = recommended[i];

        // check if the item is already in the array
        const foundIndex = arr.findIndex(item => item === recItem.skuNumber);
        if(foundIndex !== -1) continue;

        const isOnPage = products.find(p => p.skuNumber === recItem.skuNumber);
        if(!isOnPage) continue;

        // find first empty place
        const index = arr.findIndex(item => item === undefined);
        if(index === -1){
            arr.push(recItem.skuNumber);
        }
        else{
            arr[index] = recItem.skuNumber;
        }
    }
    console.log('add recommended items in their places', arr);

    // add all not recommended and not purchased
    const purchasedSKUList = [];
    for(let i=0; i<products.length; i++){
        const product = products[i];

        const alreadyAdded = arr.findIndex(skuNumber => skuNumber === product.skuNumber) !== -1;
        if(alreadyAdded) continue;

        const isPurchased = purchased.findIndex((p: IPurchasedProduct) => p.skuNumber === product.skuNumber) !== -1;
        if(isPurchased) {
            purchasedSKUList.push(product.skuNumber);
        }
        else{
            const index = arr.findIndex(item => item === undefined);
            if(index === -1){
                arr.push(product.skuNumber);
            }
            else{
                arr[index] = product.skuNumber;
            }
        }
    }
    console.log('add all not recommended and not purchased', arr);

    // add all purchased at the end
    for(let i=0; i<purchasedSKUList.length; i++){
        arr.push(purchasedSKUList[i]);
    }
    console.log('add purchased', arr);

    // set main order according to the created array
    for(let i=0; i<arr.length; i++){
        const skuNumber = arr[i];
        const found = products.find(p => p.skuNumber === skuNumber);
        if(!found) continue;

        found.mainOrder = i;
    }

    return products;
};

